import { signal } from '@preact/signals-react';
import type { TranslationControllerTranslationLanguage } from '@/api-gen/api-v3.schemas';

export interface LanguageOption {
  value: string; // languageCode for API calls
  twoLetterCode: string; // twoLetterCode for flag display
  label: string; // displayName for UI
}

// Available languages from API
export const availableLanguages = signal<TranslationControllerTranslationLanguage[]>([]);
export const availableLanguagesLoading = signal<boolean>(false);
export const availableLanguagesError = signal<string | null>(null);

// Selected language codes - store both code types
export const selectedSourceLanguageCode = signal<string>('');
export const selectedSourceTwoLetterCode = signal<string>('');
export const selectedTargetLanguageCode = signal<string>('');
export const selectedTargetTwoLetterCode = signal<string>('');

// Saga-specific language codes to solve race condition
export const sagaSourceLanguageCode = signal('');
export const sagaTargetLanguageCode = signal('');

// Atomic setter for saga language codes
export const setSagaLanguages = (source: string, target: string) => {
  sagaSourceLanguageCode.value = source;
  sagaTargetLanguageCode.value = target;
};

// Helper function to convert API language to LanguageOption format
export const mapApiLanguagesToOptions = (
  languages: TranslationControllerTranslationLanguage[]
): LanguageOption[] => {
  return languages.map((lang) => ({
    value: lang.languageCode || '',
    twoLetterCode: lang.twoLetterCode || '',
    label: lang.displayName || '',
  }));
};

// Helper function to get Language options in the format the autocomplete expects
export const getLanguageOptions = (): LanguageOption[] => {
  return mapApiLanguagesToOptions(availableLanguages.value);
};

// Format two-letter code for flag-icons compatibility
// This ensures codes like 'en-US' are properly formatted for the flag icons library
export const formatCodeForFlag = (code: string): string => {
  if (!code) return '';

  // Welsh flag
  if (code === 'cy-GB') {
    return 'gb-wls';
  }

  // Catalan flag
  if (code === 'ca-ES') {
    return 'es-ct';
  }

  // Arab League flag
  if (code === 'ar-AE') {
    return 'arab';
  }

  // Swahili
  if (code === 'sw-KE') {
    return 'ke';
  }

  // Kannada
  if (code === 'kn-IN') {
    return 'kn';
  }

  // Check if the code contains a hyphen (like 'en-US')
  if (code.includes('-')) {
    // Extract the second part after the hyphen
    const twoLetterCode = code.split('-')[1].toLowerCase();
    return twoLetterCode;
  }

  // If it's already a two-letter code like 'us', just use it as is
  return code.toLowerCase();
};

// Helper function to find a language by either code
export const findLanguageByCode = (code: string): TranslationControllerTranslationLanguage | undefined => {
  return availableLanguages.value.find(lang => 
    lang.languageCode === code || lang.twoLetterCode === code
  );
};

// Helper function to set a source language by code
export const setSourceLanguage = (code: string): void => {
  const language = findLanguageByCode(code);
  if (!language) return;
  
  selectedSourceLanguageCode.value = language.languageCode || '';
  selectedSourceTwoLetterCode.value = language.twoLetterCode || '';
};

// Helper function to set a target language by code
export const setTargetLanguage = (code: string): void => {
  const language = findLanguageByCode(code);
  if (!language) return;
  
  selectedTargetLanguageCode.value = language.languageCode || '';
  selectedTargetTwoLetterCode.value = language.twoLetterCode || '';
};
